import type { ApolloError } from '@apollo/client';

/**
 * Convert any Apollo error (or plain Error) into a UI-ready message and
 * correlation id. Backend sanitises internal details; this helper just
 * pulls the fields out in a consistent way.
 *
 * Contract with the backend:
 *   - The first entry in graphQLErrors has `extensions.code` and
 *     `extensions.requestId`.
 *   - Sanitised codes (FORBIDDEN / NOT_FOUND / CONFLICT / INTERNAL_SERVER_ERROR)
 *     arrive with a generic message; we display it as-is.
 *   - User-facing codes (BAD_USER_INPUT / UNAUTHENTICATED) carry a specific
 *     message intended for the user.
 *   - Network errors (no graphQLErrors) fall back to a connectivity hint.
 */
export interface UIError {
  message: string;
  code?: string;
  requestId?: string;
}

const NETWORK_MESSAGE =
  'Could not reach the server. Check your connection and try again.';

export function toUIError(error: unknown): UIError {
  if (!error) return { message: 'Unknown error' };

  // ApolloError shape
  if (typeof error === 'object' && error !== null) {
    const ae = error as ApolloError;
    if (ae.graphQLErrors && ae.graphQLErrors.length > 0) {
      const first = ae.graphQLErrors[0];
      return {
        message: first.message,
        code: first.extensions?.code as string | undefined,
        requestId: first.extensions?.requestId as string | undefined,
      };
    }
    if (ae.networkError) {
      return { message: NETWORK_MESSAGE, code: 'NETWORK_ERROR' };
    }
    if ((error as Error).message) {
      return { message: (error as Error).message };
    }
  }

  return { message: String(error) };
}

/**
 * Format a UIError for inline display.
 *
 * The backend returns a W3C traceId (32 hex chars) as `requestId`. We
 * only show the first 8 chars to the user — long enough to be unique
 * for ops searches, short enough to read aloud on a support call. The
 * full id is preserved on the `UIError` so a future "copy ref" affordance
 * can surface it in its entirety.
 */
export function formatErrorMessage(err: UIError): string {
  if (err.requestId) return `${err.message} (ref: ${shortRef(err.requestId)})`;
  return err.message;
}

/**
 * Short form of a request/trace ref shown to users. Matches the backend's
 * `shortTraceId` helper (first 8 chars of the W3C traceId) so a ref
 * pasted from the UI maps directly to log lines quickly — the operator
 * can grep `.logs/backend.jsonl` by the short form and jump to the full
 * traceId from there.
 */
export function shortRef(requestId: string): string {
  return requestId.slice(0, 8);
}

// ── Email validation (mirrors the backend lib/validation.ts) ───────────────

/**
 * Match the backend email regex exactly. Keeping these in sync manually —
 * simpler than sharing a package across workspaces for 3 lines of code.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 254) return false;
  if (trimmed.includes('..')) return false;
  return EMAIL_RE.test(trimmed);
}

// ── Phone validation (mirrors the backend lib/validation.ts) ───────────────

/**
 * Accept a phone if it has at least 10 digits. A single leading `+` is
 * permitted and not counted; spaces, dashes, parens, and dots are
 * tolerated as separators. Mirror of `isValidPhone` in the backend.
 */
export function isValidPhone(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 32) return false;
  const plusIdx = trimmed.indexOf('+');
  if (plusIdx > 0) return false;
  const rest = plusIdx === 0 ? trimmed.slice(1) : trimmed;
  if (rest.includes('+')) return false;
  if (!/^[\d\s().-]*$/.test(rest)) return false;
  const digitCount = (rest.match(/\d/g) ?? []).length;
  return digitCount >= 10;
}
